import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";

type AlbumArtworkProps = {
  lightSrc?: string;
  darkSrc?: string;
  alt: string;
  /** The album itself is still on its way, so there is no `src` to wait on yet. */
  isLoading?: boolean;
  /** The panel: its size in the card, and any padding around the picture. */
  className?: string;
  /**
   * The picture's box inside the panel. Give it the artwork's ratio where the
   * card has no height of its own, or the panel collapses until it loads.
   */
  frameClassName?: string;
  /** Where the picture sits in its box, e.g. `object-bottom`. */
  imageClassName?: string;
  /** The loading placeholder's shape, roughly the artwork's outline. */
  placeholderClassName?: string;
};

/**
 * A slide's picture from a CMS album: a transparent PNG laid over a panel
 * painted with the brand's secondary background (native's cards use the same
 * token), so one set of artwork works on any surface.
 *
 * The dark picture is a `<source>` for `prefers-color-scheme: dark`, which is
 * all `useDarkMode` follows, so the browser fetches only the one it shows. A
 * `src` chosen from `isDarkMode` would stay light after hydration — the server
 * renders light and React does not patch a differing attribute.
 *
 * The picture is keyed on its sources and stays transparent until it has
 * loaded, behind a placeholder. Swapping the `src` of one element instead kept
 * the previous slide's picture on screen until the next one had decoded.
 *
 * Where the album does not exist, the panel shows on its own. A dark-mode
 * visitor falls back to the light picture if the dark album is missing.
 *
 * @see useAlbumArtwork
 */
const AlbumArtwork = ({
  lightSrc,
  darkSrc,
  alt,
  isLoading = false,
  className,
  frameClassName,
  imageClassName = "object-bottom",
  placeholderClassName = "inset-0 rounded-lg",
}: AlbumArtworkProps) => {
  const dark = darkSrc ?? lightSrc;
  // Which picture the browser picks is its business; whichever it is, `onLoad`
  // reports it, so readiness is tracked per pair of sources.
  const key = `${lightSrc ?? ""}|${dark ?? ""}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const isReady = loadedKey === key;
  const hasPicture = !!(lightSrc || dark);
  const showPlaceholder = hasPicture ? !isReady : isLoading;

  const markLoaded = useCallback(() => setLoadedKey(key), [key]);

  return (
    <div
      aria-busy={showPlaceholder}
      className={cn(
        "flex overflow-hidden bg-bg-brand-secondary-default",
        className,
      )}
    >
      <div className={cn("relative h-full w-full", frameClassName)}>
        {showPlaceholder ? (
          <div
            aria-hidden="true"
            className={cn(
              "absolute animate-pulse bg-container-neutral-minimal",
              placeholderClassName,
            )}
          />
        ) : null}
        {hasPicture ? (
          <picture key={key}>
            {dark && dark !== lightSrc ? (
              <source media="(prefers-color-scheme: dark)" srcSet={dark} />
            ) : null}
            <img
              src={lightSrc ?? dark}
              alt={alt}
              decoding="async"
              onLoad={markLoaded}
              // A picture already in the cache can finish before React attaches
              // `onLoad`, and then the event is never seen.
              ref={(image) => {
                if (image?.complete && image.naturalWidth > 0) markLoaded();
              }}
              className={cn(
                "absolute inset-0 h-full w-full object-contain transition-opacity duration-200",
                isReady ? "opacity-100" : "opacity-0",
                imageClassName,
              )}
            />
          </picture>
        ) : null}
      </div>
    </div>
  );
};

export default AlbumArtwork;
